import { ASSESSMENT_SLUG, RELATIONSHIP_TO_PATIENT } from "core/consts";
import { ONTOLOGIES } from "core/model/utils/ontologies";
import { useGetOntology } from "core/model/utils/ontologies/hooks";
import { Auction, GetOntologyType } from "core/types";
import { useEncryption } from "dsl/atoms/Contexts";
import { PatientInfoSlugContext } from "dsl/organisms/PatientProfileDetailsShared";
import {
  BodyWrapper,
  EncryptedField,
  getAdmissionsMainContactValue,
  PatientProfileInfoCard,
  StringValue,
} from "dsl/organisms/PatientProfileDetailsShared/shared";
import { useTranslations } from "translations";
import Translations from "translations/types";

function MainContactRelationshipField({
  auction,
  getOntology,
  isSealdOnly,
  translations,
  withEncryptionAccess,
}: {
  auction: Auction;
  getOntology: GetOntologyType;
  isSealdOnly: boolean;
  translations: Translations;
  withEncryptionAccess: boolean;
}) {
  const profile = auction?.admissions?.profile;
  const relationshipLabel =
    translations.careproviderApp.recareAdmissionsForm.contactPersonInformation
      .sectionRelationship;

  if (profile?.is_patient_main_contact) {
    return (
      <BodyWrapper>
        <b className="-mr-3 font-bold">{`${relationshipLabel}${translations.general.colon}`}</b>
        <StringValue
          testId="admissions_relationship_to_patient"
          value={
            translations.careproviderApp.recareAdmissionsProfile
              .patientIsContactPerson
          }
        />
      </BodyWrapper>
    );
  }

  if (profile?.relationship_other) {
    return (
      <EncryptedField
        isBold
        isSealdOnly={isSealdOnly}
        prefix={relationshipLabel}
        testId="admissions_relationship_other"
        value={profile.relationship_other}
        withEncryptionAccess={withEncryptionAccess}
      />
    );
  }

  return (
    <BodyWrapper>
      <b className="-mr-3 font-bold">{`${relationshipLabel}${translations.general.colon}`}</b>
      <StringValue
        testId="admissions_relationship_to_patient"
        value={getOntology({
          key:
            profile?.relationship_to_patient ?? RELATIONSHIP_TO_PATIENT.OTHER,
          type: ONTOLOGIES.RELATIONSHIP_TO_PATIENT,
        })}
      />
    </BodyWrapper>
  );
}

export function MainContactInformation({
  auction,
  getOntology,
  isSealdOnly,
  translations,
  withEncryptionAccess,
}: {
  isSealdOnly: boolean;
  translations: Translations;
  auction: Auction;
  withEncryptionAccess: boolean;
  getOntology: GetOntologyType;
}) {
  const { firstName, lastName } = getAdmissionsMainContactValue(auction);

  return (
    <>
      <EncryptedField
        isBold
        isSealdOnly={isSealdOnly}
        prefix={
          translations.careproviderApp.recareAdmissionsForm.patientInformation
            .firstNameLabel
        }
        testId="admissions_first_name"
        value={firstName}
        withEncryptionAccess={withEncryptionAccess}
      />
      <EncryptedField
        isBold
        isSealdOnly={isSealdOnly}
        prefix={
          translations.careproviderApp.recareAdmissionsForm.patientInformation
            .lastNameLabel
        }
        testId="admissions_last_name"
        value={lastName}
        withEncryptionAccess={withEncryptionAccess}
      />

      <MainContactRelationshipField
        auction={auction}
        getOntology={getOntology}
        isSealdOnly={isSealdOnly}
        translations={translations}
        withEncryptionAccess={withEncryptionAccess}
      />

      {auction?.admissions?.profile?.main_contact_phone ? (
        <EncryptedField
          isBold
          isSealdOnly={isSealdOnly}
          prefix={
            translations.careproviderApp.recareAdmissionsForm
              .contactPersonInformation.phoneNumber
          }
          testId="admissions_main_contact_phone"
          value={auction.admissions?.profile?.main_contact_phone}
          withEncryptionAccess={withEncryptionAccess}
        />
      ) : (
        <BodyWrapper>
          <b className="-mr-3 font-bold">{`${translations.careproviderApp.recareAdmissionsForm.contactPersonInformation.phoneNumber}${translations.general.colon}`}</b>
          <StringValue
            testId="admissions_main_contact_phone"
            value={translations.careproviderApp.recareAdmissionsProfile.unknown}
          />
        </BodyWrapper>
      )}
      <BodyWrapper>
        <b className="-mr-3 font-bold">{`${translations.careproviderApp.recareAdmissionsForm.contactPersonInformation.emailAddress}${translations.general.colon}`}</b>
        <StringValue
          style={{
            wordBreak: "break-all",
          }}
          testId="admissions_main_contact_email_address"
          value={
            auction?.patient?.social_worker?.email ??
            translations.careproviderApp.recareAdmissionsProfile.unknown
          }
        />
      </BodyWrapper>
    </>
  );
}

export function AdmissionsMainContactCard({ auction }: { auction: Auction }) {
  const translations = useTranslations();
  const { isSealdOnly, withEncryptionAccess } = useEncryption();
  const getOntology = useGetOntology();

  return (
    <PatientInfoSlugContext.Provider
      value={{
        assessmentSlug: ASSESSMENT_SLUG.INFO,
        auctionId: auction.id,
        patientId: auction.patient.id,
      }}
    >
      <PatientProfileInfoCard
        testId="main_contact_information_category_container"
        title={
          translations.careproviderApp.recareAdmissionsForm
            .contactPersonInformation.title
        }
      >
        <MainContactInformation
          auction={auction}
          getOntology={getOntology}
          isSealdOnly={isSealdOnly}
          translations={translations}
          withEncryptionAccess={withEncryptionAccess}
        />
      </PatientProfileInfoCard>
    </PatientInfoSlugContext.Provider>
  );
}
